import React, { FC } from 'react';
import { Text, View } from 'react-native';
import { OperationSearchItem, ProductionBatchStatistics } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { machineStatus as machineStatuses, ncFileStatuses } from '@helpers';
import Svg from '@styles/icons/svg';
import { useOperatorPanelStatuses } from '@components/operatorPanel/hooks/useOperatorPanelStatuses';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { PanelProps } from '@components/operatorPanel/components/panel';

interface NCFileJobsInfoProps extends Pick<PanelProps, 'CC' | 'isOperator' | 'scale' | 'font'> {
  jobs: ReducerEntityWithLoadingState<OperationSearchItem[]>;
  ncFileName: string;
  ncFileStatus: (typeof ncFileStatuses)[keyof typeof ncFileStatuses];
  isWithERP: boolean | null;
  machineStatus: string;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
}

const NCFileJobsInfoComponent: FC<NCFileJobsInfoProps> = ({
  ncFileName,
  ncFileStatus,
  jobs,
  CC,
  isWithERP,
  machineStatus,
  batch,
  scale = initialScale,
  font = initialFont,
  isOperator = true,
}) => {
  const {
    batchLinked,
    batchNotLinked,
    machineIsActive,
    machineIsChanged,
    machineIsNotProductive,
    ncFileChange,
    ncFileIgnored,
    ncFileLinked,
    ncFileUnknown,
    ncFileSetup,
  } = useOperatorPanelStatuses({ machineStatus, ncFileStatus, isOperator });

  const blockHeight = 24;

  const titleStyles = [font['Subhead 1'], { color: CC('Label/Light/Always Light Tertiary'), minHeight: scale(blockHeight) }];
  const valueStyles = [font.Body, { color: CC('Label/Light/AlwaysLight Secondary'), minHeight: scale(blockHeight) }];
  const jobRightValueStyles = [font.Body, { color: CC('Label/Light/Always Light Tertiary'), minHeight: scale(blockHeight) }];

  const jobsLoaded = jobs.wasLoaded;

  const shouldDisplayJobs = jobsLoaded && ncFileLinked && batchLinked && isOperator && isWithERP;

  const shouldDisplayNotLinkedToJobs = batchNotLinked && ncFileLinked && isWithERP;

  const shouldDisplayUnknownNCFileType = ncFileUnknown;

  const shouldDisplayNCFileInfo = !ncFileChange && !ncFileIgnored && !ncFileSetup && ncFileName;

  if (!shouldDisplayNCFileInfo) return null;

  return (
    <View style={{ maxWidth: scale(360), gap: scale(18) }}>
      <View style={{ width: '100%' }}>
        <Text style={titleStyles}>{i18n.t('NC FILE')}</Text>
        <Text testID={TEST_IDS.operatorPanel.home.ncFileName} numberOfLines={1} style={valueStyles}>
          {ncFileName}
        </Text>
      </View>

      {shouldDisplayUnknownNCFileType && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
          <Svg icon="unknownLink" width={scale(24)} height={scale(24)} />
          <Text numberOfLines={1} style={valueStyles}>
            {i18n.t('Unknown NC File Type')}
          </Text>
        </View>
      )}

      {shouldDisplayNotLinkedToJobs && <Text style={titleStyles}>{i18n.t('NOT LINKED TO JOB')}</Text>}

      {shouldDisplayJobs && (
        <View style={{ width: '100%' }}>
          <Text style={titleStyles}>{i18n.t(jobs.data.length === 1 ? 'JOB' : 'JOBS')}</Text>
          {jobs.data?.length > 3 && (
            <Text numberOfLines={1} style={valueStyles}>
              {i18n.t('{{jobsAmount}} Jobs Linked', { jobsAmount: jobs.data.length })}
            </Text>
          )}
          {jobs.data?.length < 4 &&
            jobs.data?.map((j) => {
              const amountOfPieces = batch?.data?.links?.find((job) => job.productionOperationId === j.productionOperationId)?.quantityTarget;

              return (
                <Text
                  style={{ minHeight: scale(blockHeight), color: CC('Label/Light/Always Light Tertiary') }}
                  key={j?.productionOperationNumber}
                  numberOfLines={1}
                >
                  <Text style={valueStyles}>{j?.productionOperationNumber} </Text>
                  <Text numberOfLines={1} style={jobRightValueStyles}>
                    {j?.operationName}{' '}
                  </Text>
                  {amountOfPieces != null && (
                    <Text numberOfLines={1} style={jobRightValueStyles}>
                      {`(${i18n.t(`{{amountOfPieces}} ${amountOfPieces > 1 ? 'Pieces' : 'Piece'}`, { amountOfPieces })})`}
                    </Text>
                  )}
                </Text>
              );
            })}
        </View>
      )}
    </View>
  );
};

const NCFileJobsInfo = connect(
  ({ details: { jobs }, membership: { isWithERP }, batch: { batch } }) => ({ jobs, isWithERP, batch }),
  {}
)(NCFileJobsInfoComponent);

export default NCFileJobsInfo;
